import type {
    ITenantDailyDistributionItem,
    ITenantServiceConsumptionItem,
} from '@experiences/interfaces';
import { useSnackbar } from '@experiences/util';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    FontVariantToken,
    Spacing,
    Typography,
} from '@uipath/apollo-core';
import {
    ApSkeleton,
    ApTypography,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { exportToCSV } from '../../../../common/utils/csvExport';
import { useConsumptionTimezone } from '../hooks/useConsumptionTimezone';
import { ConsumptionAnalyticsCard } from './ConsumptionAnalyticsCard';
import { ConsumptionAnalyticsExportDialog } from './ConsumptionAnalyticsExportDialog';
import {
    ConsumptionByServices,
    getConsumptionServiceDisplayName,
} from './ConsumptionByServices';
import { DistributionOfConsumption } from './DistributionOfConsumption';
import { flatActionButtonStyle } from './flatActionButtonStyle';
import { SkeletonConsumptionByServices } from './skeletons/SkeletonConsumptionByServices';

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            width: '100%',
            overflow: 'visible',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: Spacing.SpacingBase,
        },
        title: { color: theme.palette.semantic.colorForeground },
        exportButton: flatActionButtonStyle(theme),
        cardsContainer: {
            display: 'flex',
            alignItems: 'stretch',
            gap: Spacing.SpacingBase,
            width: '100%',
            overflow: 'visible',
        },
        skeletonChart: {
            width: '100%',
            height: 380,
            borderRadius: Border.BorderRadiusM,
        },
    })
);

interface ConsumptionAnalyticsProps {
    unitTypeDisplay?: string;
    distributionData: ITenantDailyDistributionItem[];
    servicesData: ITenantServiceConsumptionItem[];
    isLoading?: boolean;
    organizationName?: string;
    tenantName?: string;
    folderName?: string;
    folderPath?: string;
    viewType?: string;
    startDateUnix?: number;
    endDateUnix?: number;
    hideHeader?: boolean;
    // Leave out tenant_name/folder/full_folder_path/view_type when this export has no such scope
    // (Consumables V2). The dashboards keep their fixed CSV schema (blank when not applicable).
    omitUnscopedColumns?: boolean;
    onExportStarted: (exportType: string, extraProps?: Record<string, unknown>) => void;
}

export const ConsumptionAnalytics: React.FC<ConsumptionAnalyticsProps> = ({
    unitTypeDisplay = 'units',
    distributionData,
    servicesData,
    isLoading = false,
    organizationName = '',
    tenantName = '',
    folderName,
    folderPath,
    viewType,
    startDateUnix,
    endDateUnix,
    hideHeader = false,
    omitUnscopedColumns = false,
    onExportStarted,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const [ exportDialogOpen, setExportDialogOpen ] = useState(false);
    const { timezoneId } = useConsumptionTimezone();

    const totalConsumption = useMemo(
        () => servicesData.reduce((sum, item) => sum + item.consumedAmount, 0),
        [ servicesData ]
    );

    const hasAnyData = totalConsumption > 0 || distributionData.some(d => d.totalConsumedAmount > 0);

    // Columns shared by both exports.
    const getExportCommonFields = useCallback(() => {
        const scoped = (value: string | undefined) => (omitUnscopedColumns && !value ? undefined : value ?? '');
        const scopedColumns = {
            tenant_name: scoped(tenantName),
            folder: scoped(folderName),
            full_folder_path: scoped(folderPath),
            view_type: scoped(viewType),
        };
        return {
            safeTenantName: (tenantName || organizationName || 'export').replaceAll(/[\\/:*?"<>|]/g, '_'),
            safeFolderName: folderName?.replaceAll(/[\\/:*?"<>|\s]+/g, '_'),
            scopeColumns: {
                organization_name: organizationName,
                ...Object.fromEntries(Object.entries(scopedColumns).filter(([ , value ]) => value !== undefined)),
                unit: unitTypeDisplay,
                export_date_utc: new Date().toISOString(),
                start_date: typeof startDateUnix === 'number' ? new Date(startDateUnix * 1000).toISOString() : '',
                end_date: typeof endDateUnix === 'number' ? new Date(endDateUnix * 1000).toISOString() : '',
                time_zone: timezoneId,
            },
        };
    }, [ omitUnscopedColumns, tenantName, organizationName, folderName, folderPath, viewType, unitTypeDisplay, startDateUnix, endDateUnix, timezoneId ]);

    const handleDistributionExport = useCallback(async () => {
        onExportStarted('DistributionOfConsumption');
        try {
            const {
                safeTenantName, safeFolderName, scopeColumns,
            } = getExportCommonFields();
            const rows = distributionData.map(item => ({
                date: item.date,
                consumed: item.totalConsumedAmount,
                ...scopeColumns,
            }));

            const fileName = safeFolderName
                ? `DistributionOfConsumption_${safeTenantName}_${safeFolderName}`
                : `DistributionOfConsumption_${safeTenantName}`;
            await exportToCSV(rows, fileName);
        } catch {
            enqueueSnackbar(translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_EXPORT_ERROR' }), { variant: 'error' });
        }
    }, [ distributionData, getExportCommonFields, enqueueSnackbar, translate, onExportStarted ]);

    const handleServicesExport = useCallback(async () => {
        onExportStarted('ConsumptionByService');
        try {
            const {
                safeTenantName, safeFolderName, scopeColumns,
            } = getExportCommonFields();
            const sortedServices = [ ...servicesData ].sort((left, right) => right.consumedAmount - left.consumedAmount);
            const rows = sortedServices.map(item => ({
                service: getConsumptionServiceDisplayName(item.service, translate),
                consumed: item.consumedAmount,
                ...scopeColumns,
                total_units: totalConsumption,
            }));

            const fileName = safeFolderName
                ? `ConsumptionByService_${safeTenantName}_${safeFolderName}`
                : `ConsumptionByService_${safeTenantName}`;
            await exportToCSV(rows, fileName);
        } catch {
            enqueueSnackbar(translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_EXPORT_ERROR' }), { variant: 'error' });
        }
    }, [ servicesData, totalConsumption, translate, getExportCommonFields, enqueueSnackbar, onExportStarted ]);

    return (
        <Box className={classes.container}>
            {!hideHeader && (
                <>
                    <Box className={classes.header}>
                        <ApTypography
                            variant={FontVariantToken.fontSizeH4Bold}
                            className={classes.title}
                        >
                            {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_TENANT_CONSUMPTION_ANALYTICS_TITLE' })}
                        </ApTypography>
                        {!isLoading && (
                            <button
                                type="button"
                                className={classes.exportButton}
                                data-cy="analytics-export-button"
                                disabled={!hasAnyData}
                                onClick={() => setExportDialogOpen(true)}
                            >
                                <DownloadOutlinedIcon sx={{ fontSize: Typography.fontSizeM }} />
                                <ApTypography variant={FontVariantToken.fontSizeSBold}>
                                    {translate({ id: 'CLIENT_CUSTOM_EXPORT' })}
                                </ApTypography>
                            </button>
                        )}
                    </Box>

                    <ConsumptionAnalyticsExportDialog
                        open={exportDialogOpen}
                        distributionData={distributionData}
                        organizationName={organizationName}
                        tenantName={tenantName}
                        folderName={folderName}
                        folderPath={folderPath}
                        viewType={viewType}
                        unitTypeDisplay={unitTypeDisplay}
                        startDateUnix={startDateUnix}
                        endDateUnix={endDateUnix}
                        onClose={() => setExportDialogOpen(false)}
                        onExportTelemetry={(granularity, selectedServicesCount, totalServicesCount) => {
                            onExportStarted('ConsumptionAnalyticsCustomExport', {
                                TimeGranularity: granularity.charAt(0).toUpperCase() + granularity.slice(1),
                                SelectedServicesCount: selectedServicesCount,
                                TotalServicesCount: totalServicesCount,
                            });
                        }}
                    />
                </>
            )}

            <Box className={classes.cardsContainer}>
                <ConsumptionAnalyticsCard
                    titleId="CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_DISTRIBUTION_TITLE"
                    isLoading={isLoading}
                    disableDownload={!hasAnyData}
                    onDownload={handleDistributionExport}
                >
                    {isLoading ? (
                        <ApSkeleton
                            variant="rectangle"
                            className={classes.skeletonChart}
                        />
                    ) : (
                        <DistributionOfConsumption
                            unitType={unitTypeDisplay}
                            data={distributionData}
                        />
                    )}
                </ConsumptionAnalyticsCard>
                <ConsumptionAnalyticsCard
                    titleId="CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_SERVICES_TITLE"
                    isLoading={isLoading}
                    disableDownload={!hasAnyData}
                    onDownload={handleServicesExport}
                >
                    {isLoading ? (
                        <SkeletonConsumptionByServices />
                    ) : (
                        <ConsumptionByServices
                            services={servicesData}
                            totalConsumption={totalConsumption}
                            unitType={unitTypeDisplay}
                        />
                    )}
                </ConsumptionAnalyticsCard>
            </Box>
        </Box>
    );
};
